'use client';

import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import type { ProposedAction } from '@/entities/proposed-actions';
import type { ProposedActionPayloadTToolInput } from '@lotushealth/lotushealth_js_sdk/schemas';
import { openDosespotActionOverlay } from '@/features/dosespot-action-form/model/dosespotActionPrefill';
import { openRxWizardOverlay } from '@/features/rx-form/model/rxWizardPrefill';
import { openLabOrderWizardOverlay } from '@/features/lab-order-form/model/labOrderWizardPrefill';
import { openDiagnoseWizardOverlay } from '@/features/diagnose-form/model/diagnoseWizardPrefill';
import {
  openPriorAuthActionOverlay,
  type PriorAuthActionMode,
} from '@/features/prior-auth-form/model/priorAuthActionPrefill';
import { openPaCompletedRxViewOverlay } from '@/features/pa-completed-rx-view/model/paCompletedRxViewPrefill';
import {
  useProposedActionMutations,
  type ProposedActionMutations,
} from '@/features/proposed-actions-plan/model/useProposedActionMutations';
import type { SourceProposedActionT } from '@/shared/lib';
import {
  nativeDraftLabOrderSetToolInputSchema,
  type SubmitLabOrderSetRequestT,
  type SubmitLabOrderSetResponseT,
  useSubmitLabOrderSetMutation,
} from '@/entities/labs';
import { useCareStateDomain } from '@/entities/care-state-domain';
import { extractApiErrorDetail } from '@/shared';
import { logger } from '@/shared/lib';
import { getTodayYYYYMMDD } from '@/shared/lib/dates';

const DOSESPOT_TOOL_NAMES = [
  'approve_refill',
  'deny_refill',
  'replace_refill',
  'approve_rxchange',
  'approve_rxchange_with_changes',
  'deny_rxchange',
] as const;

const DOSESPOT_TOOLS = new Set<string>(DOSESPOT_TOOL_NAMES);

const PA_ACTION_TOOL_TO_MODE = {
  answer_prior_auth_question: 'answer_question',
  upload_prior_auth_attachment: 'upload_attachment',
  submit_prior_auth: 'submit',
  cancel_prior_auth: 'cancel',
  appeal_prior_auth: 'appeal',
  deny_prior_auth: 'deny',
} as const satisfies Record<string, PriorAuthActionMode>;

type PaActionToolName = keyof typeof PA_ACTION_TOOL_TO_MODE;

const PA_ACTION_TOOLS = new Set<string>(Object.keys(PA_ACTION_TOOL_TO_MODE));

function isPaActionToolName(toolName: string): toolName is PaActionToolName {
  return PA_ACTION_TOOLS.has(toolName);
}

const PA_COMPLETED_TOOL = 'review_completed_prior_auth';
const PA_SEND_APPROVED_TOOL = 'send_approved_prior_auth_to_pharmacy';

const PA_COMPLETED_VIEW_TOOLS = new Set<string>([PA_COMPLETED_TOOL, PA_SEND_APPROVED_TOOL]);

const CLIENT_TOOLS = new Set<string>([
  'draft_prescription',
  'draft_existing_lab',
  'draft_lab_order_set',
  'draft_native_lab_order_set',
  'send_follow_up',
  'diagnose',
  ...DOSESPOT_TOOL_NAMES,
  ...PA_ACTION_TOOLS,
  PA_COMPLETED_TOOL,
  PA_SEND_APPROVED_TOOL,
]);

export interface ProposedActionExecutor {
  execute: (action: ProposedAction) => Promise<boolean>;
  revise: (action: ProposedAction, revisionReason: string) => void;
  hasClientExecution: (toolName: string) => boolean;
}

type RevisionContext = Readonly<{ isRevision: true; revisionReason: string }>;

function toNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toStr(value: unknown): string {
  return typeof value === 'string' ? value : String(value ?? '');
}

function toStrArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(toStr).filter((item) => item.trim().length > 0);
}

function toIcdCodeStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null) {
        const code = (item as Record<string, unknown>).code;
        return typeof code === 'string' ? code : '';
      }
      return '';
    })
    .filter((code) => code.trim().length > 0);
}

function buildSource(externalId: string, revision?: RevisionContext): SourceProposedActionT {
  return revision
    ? { externalId, isRevision: true, revisionReason: revision.revisionReason }
    : { externalId };
}

export function buildLabOrderSetRequestFromToolInput(
  input: ProposedActionPayloadTToolInput
): SubmitLabOrderSetRequestT {
  return {
    name: toStr(input.name),
    description: toStr(input.description),
    collectionMethod: toStr(
      input.collectionMethod
    ) as SubmitLabOrderSetRequestT['collectionMethod'],
    markerIds: toStrArray(input.markerIds),
    fasting: input.fasting == null ? undefined : input.fasting === true,
    icdCodes: toIcdCodeStrings(input.icdCodes),
    notes: input.notes != null ? toStr(input.notes) : undefined,
    priority: input.priority === true,
  };
}

interface ExecuteLabOrderSetActionArgs {
  mode: 'execute' | 'revise';
  externalId: string;
  input: ProposedActionPayloadTToolInput;
  patientUserId: string;
  submitLabOrderSet: (variables: {
    userId: string;
    data: SubmitLabOrderSetRequestT;
  }) => Promise<SubmitLabOrderSetResponseT>;
  mutations: Pick<ProposedActionMutations, 'execute' | 'rejectWithRevision'>;
}

export async function executeLabOrderSetAction({
  mode,
  externalId,
  input,
  patientUserId,
  submitLabOrderSet,
  mutations,
}: ExecuteLabOrderSetActionArgs): Promise<boolean> {
  try {
    const response = await submitLabOrderSet({
      userId: patientUserId,
      data: buildLabOrderSetRequestFromToolInput(input),
    });
    const toolOutput = {
      labTestId: response.labTest.id,
      remoteOrderId: response.order.remote_id,
    };
    if (mode === 'revise') {
      await mutations.rejectWithRevision(externalId, toolOutput);
      return true;
    }
    await mutations.execute(externalId, toolOutput);
    return true;
  } catch (err: unknown) {
    const message = extractApiErrorDetail(err) ?? 'Could not submit lab set.';
    toast.error('Cannot submit lab set', { description: message });
    logger.error(`proposed-actions.${mode}.draftLabOrderSetFailed`, {
      toolName: 'draft_lab_order_set',
      externalId,
      err,
    });
    return false;
  }
}

function openRxWizard(
  input: ProposedActionPayloadTToolInput,
  externalId: string,
  revision?: RevisionContext
): void {
  openRxWizardOverlay({
    kind: 'coded',
    dispensableDrugId: toNumber(input.dispensableDrugId, 0),
    directions: toStr(input.directions),
    quantity: toStr(input.quantity),
    refills: toStr(input.refills),
    daysSupply: toStr(input.daysSupply),
    pharmacyId: input.pharmacyId != null ? toNumber(input.pharmacyId, 0) : undefined,
    pharmacyNotes: input.pharmacyNotes != null ? toStr(input.pharmacyNotes) : undefined,
    noSubstitutions: input.noSubstitutions === true,
    effectiveDate: getTodayYYYYMMDD(),
    sourceProposedAction: buildSource(externalId, revision),
  });
}

function openLabWizard(
  input: ProposedActionPayloadTToolInput,
  externalId: string,
  revision?: RevisionContext
): void {
  const rawIcdCodes = input.icdCodes;
  const icdCodes = Array.isArray(rawIcdCodes)
    ? rawIcdCodes
        .filter(
          (c): c is { code: string; description: string } =>
            typeof c === 'object' &&
            c !== null &&
            typeof (c as Record<string, unknown>).code === 'string'
        )
        .map((c) => ({ code: c.code, description: c.description ?? '' }))
    : undefined;

  openLabOrderWizardOverlay({
    type: 'existing_test',
    labTestId: toStr(input.labTestId),
    labTestName: input.labTestName != null ? toStr(input.labTestName) : undefined,
    icdCodes,
    notes: input.notes != null ? toStr(input.notes) : undefined,
    priority: input.priority === true,
    sourceProposedAction: buildSource(externalId, revision),
  });
}

export function openNativeLabOrderSetWizard(
  action: Pick<ProposedAction, 'external_id' | 'payload'>,
  revision?: RevisionContext
): boolean {
  const parsedInput = nativeDraftLabOrderSetToolInputSchema.safeParse(action.payload.tool_input);
  if (!parsedInput.success) {
    toast.error('Cannot open lab order set', {
      description: 'This proposed action contains invalid native lab order data.',
    });
    logger.error('proposed-actions.invalidNativeLabOrderSet', {
      externalId: action.external_id,
    });
    return false;
  }

  const input = parsedInput.data;
  openLabOrderWizardOverlay({
    type: 'native_order_set',
    labSlug: input.labSlug,
    collectionMethod: input.collectionMethod,
    labTestIds: [],
    markerIds: input.markerIds,
    icdCodes: input.icdCodes,
    notes: input.notes,
    priority: input.priority ?? false,
    sourceProposedAction: buildSource(action.external_id, revision),
  });
  return true;
}

function openDiagnoseWizard(
  input: ProposedActionPayloadTToolInput,
  externalId: string,
  revision?: RevisionContext
): void {
  const code = toStr(input.icd10Code).trim();

  openDiagnoseWizardOverlay({
    icd10Code: code || undefined,
    note: input.note != null ? toStr(input.note) : undefined,
    sourceProposedAction: buildSource(externalId, revision),
  });
}

function openDosespotActionForm(action: ProposedAction, revision?: RevisionContext): void {
  openDosespotActionOverlay({
    action,
    sourceProposedAction: buildSource(action.external_id, revision),
  });
}

function openPriorAuthActionForm(
  action: ProposedAction,
  mode: PriorAuthActionMode,
  revision?: RevisionContext
): void {
  const input = action.payload.tool_input;
  const caseId = toNumber(input.priorAuthId, 0);
  const recordId = input.priorAuthRecordId != null ? toStr(input.priorAuthRecordId) : '';
  const pendingQuestionIdRaw = input.pendingQuestionId;
  const pendingQuestionId =
    pendingQuestionIdRaw != null ? toNumber(pendingQuestionIdRaw, 0) || null : null;

  openPriorAuthActionOverlay({
    caseId,
    recordId,
    mode,
    pendingQuestionId,
    patientUserId: input.patientUserId != null ? toStr(input.patientUserId) : undefined,
    dosespotPrescriptionId:
      input.dosespotPrescriptionId != null ? toStr(input.dosespotPrescriptionId) : undefined,
    action,
    sourceProposedAction: buildSource(action.external_id, revision),
  });
}

function openPaCompletedRxView(action: ProposedAction, revision?: RevisionContext): void {
  const input = action.payload.tool_input;
  openPaCompletedRxViewOverlay({
    patientUserId: input.patientUserId != null ? toStr(input.patientUserId) : '',
    priorAuthRecordId: input.priorAuthRecordId != null ? toStr(input.priorAuthRecordId) : '',
    prescriptionRecordId:
      input.prescriptionRecordId != null ? toStr(input.prescriptionRecordId) : '',
    dosespotPrescriptionId:
      input.dosespotPrescriptionId != null ? toStr(input.dosespotPrescriptionId) : '',
    authorizationNumber:
      input.authorizationNumber != null ? toStr(input.authorizationNumber) : null,
    status: input.status != null ? toStr(input.status) : '',
    sourceProposedAction: buildSource(action.external_id, revision),
    approvedQuantity: input.approvedQuantity != null ? toStr(input.approvedQuantity) : null,
    approvedDaysSupply: input.approvedDaysSupply != null ? toStr(input.approvedDaysSupply) : null,
    approvedRefills: input.approvedRefills != null ? toStr(input.approvedRefills) : null,
  });
}

function routeTool(
  mode: 'execute',
  action: ProposedAction,
  mutations: Pick<ProposedActionMutations, 'execute' | 'rejectWithRevision'>,
  patientUserId: string,
  submitLabOrderSet: (variables: {
    userId: string;
    data: SubmitLabOrderSetRequestT;
  }) => Promise<SubmitLabOrderSetResponseT>
): Promise<boolean>;
function routeTool(
  mode: 'revise',
  action: ProposedAction,
  mutations: Pick<ProposedActionMutations, 'execute' | 'rejectWithRevision'>,
  patientUserId: string,
  submitLabOrderSet: (variables: {
    userId: string;
    data: SubmitLabOrderSetRequestT;
  }) => Promise<SubmitLabOrderSetResponseT>,
  revisionReason: string
): Promise<boolean>;
async function routeTool(
  mode: 'execute' | 'revise',
  action: ProposedAction,
  mutations: Pick<ProposedActionMutations, 'execute' | 'rejectWithRevision'>,
  patientUserId: string,
  submitLabOrderSet: (variables: {
    userId: string;
    data: SubmitLabOrderSetRequestT;
  }) => Promise<SubmitLabOrderSetResponseT>,
  revisionReason?: string
): Promise<boolean> {
  const { tool_name: toolName, tool_input: input } = action.payload;
  const externalId = action.external_id;
  const revision: RevisionContext | undefined =
    mode === 'revise' ? { isRevision: true, revisionReason: revisionReason! } : undefined;

  if (DOSESPOT_TOOLS.has(toolName)) {
    openDosespotActionForm(action, revision);
    logger.info(`proposed-actions.${mode}.triggered`, { toolName, externalId });
    return true;
  }

  if (isPaActionToolName(toolName)) {
    openPriorAuthActionForm(action, PA_ACTION_TOOL_TO_MODE[toolName], revision);
    logger.info(`proposed-actions.${mode}.triggered`, { toolName, externalId });
    return true;
  }

  if (PA_COMPLETED_VIEW_TOOLS.has(toolName)) {
    openPaCompletedRxView(action, revision);
    logger.info(`proposed-actions.${mode}.triggered`, { toolName, externalId });
    return true;
  }

  switch (toolName) {
    case 'draft_prescription':
      openRxWizard(input, externalId, revision);
      break;
    case 'draft_existing_lab':
      openLabWizard(input, externalId, revision);
      break;
    case 'diagnose':
      openDiagnoseWizard(input, externalId, revision);
      break;
    case 'draft_lab_order_set': {
      const succeeded = await executeLabOrderSetAction({
        mode,
        externalId,
        input,
        patientUserId,
        submitLabOrderSet,
        mutations,
      });
      if (!succeeded) return false;
      break;
    }
    case 'draft_native_lab_order_set':
      if (!openNativeLabOrderSetWizard(action, revision)) return false;
      break;
    case 'send_follow_up': {
      const call =
        mode === 'revise'
          ? mutations.rejectWithRevision(externalId)
          : mutations.execute(externalId);
      try {
        await call;
      } catch (err: unknown) {
        logger.error(`proposed-actions.${mode}.sendFollowUpFailed`, { toolName, externalId, err });
        return false;
      }
      break;
    }
    default:
      logger.warn(`proposed-actions.${mode}.unsupported`, { toolName, externalId });
      return false;
  }

  logger.info(`proposed-actions.${mode}.triggered`, { toolName, externalId });
  return true;
}

export function useProposedActionExecutor(): ProposedActionExecutor {
  const mutations = useProposedActionMutations();
  const { patientUserId } = useCareStateDomain();
  const submitLabOrderSetMutation = useSubmitLabOrderSetMutation();

  const hasClientExecution = useCallback(
    (toolName: string): boolean => CLIENT_TOOLS.has(toolName),
    []
  );

  const execute = useCallback(
    (action: ProposedAction): Promise<boolean> =>
      routeTool('execute', action, mutations, patientUserId, submitLabOrderSetMutation.mutateAsync),
    [mutations, patientUserId, submitLabOrderSetMutation.mutateAsync]
  );

  const revise = useCallback(
    (action: ProposedAction, revisionReason: string): void => {
      void routeTool(
        'revise',
        action,
        mutations,
        patientUserId,
        submitLabOrderSetMutation.mutateAsync,
        revisionReason
      );
    },
    [mutations, patientUserId, submitLabOrderSetMutation.mutateAsync]
  );

  return useMemo(
    () => ({ execute, revise, hasClientExecution }),
    [execute, revise, hasClientExecution]
  );
}
